/**
 * The privacy consent contract shared by every surface and the API.
 *
 * Three places ask the visitor the same question (the marketing site banner,
 * the app's consent sheet on web and native) and one place has to answer it on
 * their behalf later: the API, when it emits a user-linked event to PostHog or
 * GA4 from a webhook or a background job where no browser is present. This
 * module is what they all agree on: the current notice version, the two
 * purposes, and the payload the app sends the API when a decision changes.
 *
 * `PRIVACY_NOTICE_VERSION` is bumped whenever the notice changes materially. A
 * stored decision under an older notice answered a different question and is
 * treated as undecided everywhere, including on the server.
 */

import { z } from "zod";

export const PRIVACY_NOTICE_VERSION = "2026-09-09" as const;

export type PrivacyNoticeVersion = typeof PRIVACY_NOTICE_VERSION;

/** One purpose's state. `unknown` means the visitor has not answered yet. */
export type ConsentStatus = "unknown" | "granted" | "denied";

export const CONSENT_STATUSES: readonly ConsentStatus[] = [
  "unknown",
  "granted",
  "denied",
];

export type ConsentDecisionSource = "prompt" | "settings";

/**
 * What the app sends to `PUT /api/users/me/privacy-consent` whenever a signed-in
 * user's decision changes (or is first seen after sign-in). Mirrors the app's
 * stored preferences one to one, so the server holds exactly what the client
 * holds and nothing more.
 */
export const privacyConsentSyncSchema = z
  .object({
    nativeIdentity: z
      .object({
        platform: z.enum(["ios", "android"]),
        appInstanceId: z.string().regex(/^[a-f0-9]{32}$/i),
      })
      .strict()
      .optional(),
    noticeVersion: z.string().min(1).max(32),
    analytics: z.enum(["unknown", "granted", "denied"]),
    marketing: z.enum(["unknown", "granted", "denied"]),
    decidedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
    source: z.enum(["prompt", "settings"]).nullable(),
  })
  .strict();

export type PrivacyConsentSync = z.infer<typeof privacyConsentSyncSchema>;

/** The stored server-side record the API reads before emitting user-linked analytics. */
export type StoredPrivacyConsent = {
  analytics: ConsentStatus;
  marketing: ConsentStatus;
  noticeVersion: string;
};

/**
 * Whether user-linked analytics may be sent for this record. Fail-closed:
 * anything other than an explicit grant under the current notice is a no.
 */
export function isAnalyticsConsentGranted(
  record: StoredPrivacyConsent | null | undefined,
): boolean {
  return (
    record?.analytics === "granted" &&
    record.noticeVersion === PRIVACY_NOTICE_VERSION
  );
}

export function isMarketingConsentGranted(
  record: StoredPrivacyConsent | null | undefined,
): boolean {
  return (
    record?.marketing === "granted" &&
    record.noticeVersion === PRIVACY_NOTICE_VERSION
  );
}
